import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

/**
 * DragContext - Shared drag/resize state across EventBlock, StaffColumn, DragOverlay.
 *
 * Supports two modes:
 *   mode: 'move'   — dragging an event to a new time/staff
 *   mode: 'resize' — dragging the bottom edge to change duration
 */

const DragCtx = createContext(null);

export function DragProvider({ children, readOnly = false }) {
  const [dragState, setDragState] = useState(null);
  // { mode, event, originStaffId, currentStaffId, currentMinutes,
  //   ghostX, ghostY,            (move only)
  //   edge, newStartMinutes, newEndMinutes }  (resize only)

  const liveRef = useRef(null);
  const columnsRef = useRef({});
  const lastDragEndRef = useRef(0);

  const registerColumn = useCallback((staffId, element) => {
    if (element) {
      columnsRef.current[staffId] = element;
    } else {
      delete columnsRef.current[staffId];
    }
  }, []);

  // ── Move ──
  const startDrag = useCallback((event, originStaffId, pointerX, pointerY, offsetY) => {
    liveRef.current = { mode: 'move', event, originStaffId, offsetY, pointerX, pointerY };
    setDragState({
      mode: 'move',
      event,
      originStaffId,
      ghostX: pointerX,
      ghostY: pointerY,
      currentStaffId: originStaffId,
      currentMinutes: event.startMinutes,
    });
  }, []);

  const updateDrag = useCallback((pointerX, pointerY, currentStaffId, currentMinutes) => {
    if (!liveRef.current) return;
    liveRef.current.pointerX = pointerX;
    liveRef.current.pointerY = pointerY;
    setDragState(prev => prev ? {
      ...prev,
      ghostX: pointerX,
      ghostY: pointerY,
      currentStaffId,
      currentMinutes,
    } : null);
  }, []);

  // ── Resize ──
  const startResize = useCallback((event, pointerY, edge = 'bottom') => {
    liveRef.current = { mode: 'resize', event, pointerY, edge };
    setDragState({
      mode: 'resize',
      event,
      originStaffId: event.staffId,
      currentStaffId: event.staffId,
      edge,
      newStartMinutes: event.startMinutes,
      newEndMinutes: event.endMinutes,
    });
  }, []);

  const updateResize = useCallback((minutes) => {
    if (!liveRef.current) return;
    const edge = liveRef.current.edge || 'bottom';
    setDragState(prev => {
      if (!prev) return null;
      if (edge === 'top') return { ...prev, newStartMinutes: minutes };
      return { ...prev, newEndMinutes: minutes };
    });
  }, []);

  // ── Shared ──
  const endDrag = useCallback(() => {
    liveRef.current = null;
    lastDragEndRef.current = Date.now();
    setDragState(null);
    setActivatedEventId(null);
  }, []);

  // ── Mobile activated state (persistent hold-ready for resize) ──
  const [activatedEventId, setActivatedEventId] = useState(null);
  const activateEvent = useCallback((eventId) => setActivatedEventId(eventId), []);
  const deactivateEvent = useCallback(() => setActivatedEventId(null), []);

  // readOnly: hosts that show the diary without DragOverlay (e.g. the agent's
  // diary card) must never start a drag — nothing would end it, and a move
  // there would not tell the client.
  const noop = useCallback(() => {}, []);

  const value = {
    dragState,
    liveRef,
    columnsRef,
    lastDragEndRef,
    registerColumn,
    startDrag: readOnly ? noop : startDrag,
    updateDrag,
    startResize: readOnly ? noop : startResize,
    updateResize,
    endDrag,
    activatedEventId,
    activateEvent: readOnly ? noop : activateEvent,
    deactivateEvent,
  };

  return (
    <DragCtx.Provider value={value}>
      {children}
    </DragCtx.Provider>
  );
}

export function useDrag() {
  const ctx = useContext(DragCtx);
  if (!ctx) throw new Error('useDrag must be used within DragProvider');
  return ctx;
}
